/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { delayedResponse, makeMockResponse } from '../mockUtils';
import {
    CashRegisterModelsListDto,
    CashRegisterModelDetailsDto,
    FactoryNumbersListDto,
    CheckSumsListDto,
    AttestationResponseDto,
    AttestationResponseStatus,
    VersionsListDto,
    ModelDocumentsListDto,
    PresignedFileInfo,
} from './cashRegistersClient.typings';
import {
    checkSumDtoFactory,
    cashRegisterDtoFactory,
    cashRegistersDetailsDtoFactory,
    cashRegistersFactoryNumbersListDto,
    versionDtoFactory,
    modelDocumentDtoFactory,
    presignedFileInfoDtoFactory,
    softwareVersionDataFactory,
} from './cashRegistersFactories';

const CASH_REGISTERS_URL = /\/cashRegisterModels$/;
const CASH_REGISTER_MODEL_URL = /\/cashRegisterModels\/(\d+)$/;
const CASH_REGISTERS_FACTORY_NUMBERS_URL = /\/cashRegisterModels\/(\d+)\/cashRegisters$/;
const MODEL_VERSIONS_URL = /\/cashRegisterModels\/(\d+)\/versions$/;
const VERSION_CHECK_SUMS_URL = /\/cashRegisterModels\/(\d+)\/versions\/(\d+)\/checksums$/;
const START_ATTESTATION_URL = /\/cashRegisterModels\/(\d+)\/startAttestation$/;
const COMPLETE_ATTESTATION_URL = /\/cashRegisterModels\/(\d+)\/endAttestation$/;
const ELIMINATE_MODEL_URL = /\/cashRegisterModels\/(\d+)\/eliminate$/;
const ACTIVATE_MODEL_URL = /\/cashRegisterModels\/(\d+)\/activate$/;
const MARK_VERSION_OUTDATED_URL = /\/cashRegisterModels\/(\d+)\/versions\/(\d+)\/outdated$/;
const ACTIVATE_VERSION_URL = /\/cashRegisterModels\/(\d+)\/versions\/(\d+)\/activate$/;
const MODEL_DOCUMENTS_URL = /\/cashRegisterModels\/(\d+)\/documents$/;
const SUBMIT_MODEL_DOCUMENTS_URL = /\/cashRegisterModels\/(\d+)\/documents\/submit$/;
const DOCUMENT_FOR_DOWNLOAD_URL = /\/cashRegisterModels\/(\d+)\/documents\/(\d+)\/url$/;
const VERSION_NAMES_URL = /\/cashRegisterModels\/(\d+)\/versionNames$/;

export function addCashRegistersMockApi(mockAdapter: MockAdapter): void {
    mockAdapter.onGet(CASH_REGISTERS_URL).reply((config) => {
        const data: CashRegisterModelsListDto = {
            count: config.params.limit * 3,
            cashRegisterModels: cashRegisterDtoFactory.list({
                count: config.params.limit,
            }),
        };
        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(MODEL_VERSIONS_URL).reply((config) => {
        let data: VersionsListDto;
        if (config.params.limit) {
            data = {
                count: config.params.limit * 3,
                versions: versionDtoFactory.list({ count: config.params.limit }),
            };
        } else {
            data = {
                count: 45,
                versions: versionDtoFactory.list({ count: 45 }),
            };
        }

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 3000);
    });

    mockAdapter.onGet(VERSION_CHECK_SUMS_URL).reply((config) => {
        const data: CheckSumsListDto = {
            count: config.params.limit * 100,
            checksums: checkSumDtoFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 300);
    });

    mockAdapter.onGet(CASH_REGISTERS_FACTORY_NUMBERS_URL).reply((config) => {
        const data: FactoryNumbersListDto = {
            count: 35,
            cashRegisters: cashRegistersFactoryNumbersListDto.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(CASH_REGISTER_MODEL_URL).reply((config) => {
        const data: CashRegisterModelDetailsDto = cashRegistersDetailsDtoFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onPatch(CASH_REGISTER_MODEL_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 100);
    });

    mockAdapter.onPost(START_ATTESTATION_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 100);
    });

    mockAdapter.onPost(COMPLETE_ATTESTATION_URL).reply((config) => {
        const data: AttestationResponseDto = {
            accepted: false,
            responseStatus: AttestationResponseStatus.FW_VERSION_OUTDATED,
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 1000);
    });

    mockAdapter.onPost(ELIMINATE_MODEL_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 1000);
    });

    mockAdapter.onPost(ACTIVATE_MODEL_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 1000);
    });

    mockAdapter.onPost(MARK_VERSION_OUTDATED_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 1000);
    });

    mockAdapter.onPost(ACTIVATE_VERSION_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 1000);
    });

    //get documents
    mockAdapter.onGet(MODEL_DOCUMENTS_URL).reply((config) => {
        const data: ModelDocumentsListDto = {
            count: 30,
            files: modelDocumentDtoFactory.list({ count: config.params.limit }),
        };

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    //upload document
    mockAdapter.onPost(MODEL_DOCUMENTS_URL).reply((config) => {
        const data: PresignedFileInfo = presignedFileInfoDtoFactory.item();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    //delete documents
    mockAdapter.onDelete(MODEL_DOCUMENTS_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 1000);
    });

    mockAdapter.onPost(SUBMIT_MODEL_DOCUMENTS_URL).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 1000);
    });

    mockAdapter.onGet(DOCUMENT_FOR_DOWNLOAD_URL).reply((config) => {
        const data = 'http://someUrl';

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 1000);
    });

    mockAdapter.onGet(VERSION_NAMES_URL).reply((config) => {
        const data = softwareVersionDataFactory.list({ count: 7 });
        softwareVersionDataFactory.resetCount();

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 3000);
    });
}
